import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@/types';
import { apiService } from '@/api';
import { CREDIT_COST } from '@/api/config';
import { isPaidPlan } from '@/utils/planHelpers';
import { getActiveOrg, useWorkspaceStore } from '@/store/workspaceStore';
import { setAnalyticsUserContext, clearAnalyticsUserContext } from '@/services/analyticsService';

// Auto-reload credits: refreshUser is the one client-side funnel where fresh
// balances land, so it nudges the server check when the balance is under the
// user's threshold. Throttled so bursts of refreshes (order polling) send at
// most one nudge per interval — the SERVER owns all real validation (lock,
// cooldown, daily cap), the client nudge is just a hint.
const AUTO_RELOAD_NUDGE_INTERVAL_MS = 90_000;
let lastAutoReloadNudge = 0;

function maybeNudgeAutoReload(user: User, userId: string, refresh: () => Promise<void>) {
  if (!user.autoReloadEnabled) return;
  if (user.remainingCredit >= (user.autoReloadThreshold ?? 10)) return;
  const now = Date.now();
  if (now - lastAutoReloadNudge < AUTO_RELOAD_NUDGE_INTERVAL_MS) return;
  lastAutoReloadNudge = now;
  void apiService.autoReloadCheck(userId).then((result) => {
    // Refresh once more so the header credits reflect the top-up right away.
    if (result.status === 'reloaded') void refresh();
  });
}

interface UserState {
  user: User | null;
  userId: string | null;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User) => void;
  setUserId: (userId: string) => void;
  clearUser: () => void;
  refreshUser: () => Promise<void>;
  decrementCredits: (amount?: number) => void;
  
  // Computed
  isPro: () => boolean;
  hasEnoughCredits: (required?: number) => boolean;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      userId: null,
      isLoading: false,

      setUser: (user) => {
        set({ user });
        // Team workspaces: drop a stale active workspace (membership revoked /
        // org expired) the moment fresh user info lands.
        useWorkspaceStore.getState().reconcileWithUser(user);
        // Update analytics context
        const { userId } = get();
        if (userId) {
          setAnalyticsUserContext({
            vivideo_user_id: userId,
            user_email: user.email,
            user_plan: user.userPlan,
            credits_remaining: user.remainingCredit,
            is_pro: isPaidPlan(user.userPlan),
            ab_group: user.abGroup,
            video_provider: user.videoProvider,
          });
        }
      },
      
      setUserId: (userId) => set({ userId }),
      
      clearUser: () => {
        set({ user: null, userId: null });
        clearAnalyticsUserContext();
      },
      
      refreshUser: async () => {
        const { userId } = get();
        if (!userId) return;
        
        set({ isLoading: true });
        try {
          const user = await apiService.getUserInfo(userId);
          set({ user, isLoading: false });
          useWorkspaceStore.getState().reconcileWithUser(user);
          maybeNudgeAutoReload(user, userId, get().refreshUser);
          // Update analytics context with refreshed user data
          setAnalyticsUserContext({
            vivideo_user_id: userId,
            user_email: user.email,
            user_plan: user.userPlan,
            credits_remaining: user.remainingCredit,
            is_pro: isPaidPlan(user.userPlan),
            ab_group: user.abGroup,
            video_provider: user.videoProvider,
          });
        } catch (error) {
          console.error('Error refreshing user:', error);
          set({ isLoading: false });
        }
      },
      
      decrementCredits: (amount = CREDIT_COST) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              remainingCredit: Math.max(0, user.remainingCredit - amount),
            },
          });
        }
      },
      
      isPro: () => {
        const { user } = get();
        // Team workspaces: inside an ACTIVE team workspace the member is
        // effectively paid (generation runs on the org's pooled credits) —
        // the personal plan only governs the personal workspace.
        const activeOrg = getActiveOrg(user);
        if (activeOrg && activeOrg.orgStatus === 'active') return true;
        return isPaidPlan(user?.userPlan);
      },
      
      hasEnoughCredits: (required = CREDIT_COST) => {
        const { user } = get();
        // Team workspaces: check the org's shared pool instead.
        const activeOrg = getActiveOrg(user);
        if (activeOrg && activeOrg.orgStatus === 'active') {
          return activeOrg.remainingCredit >= required;
        }
        return (user?.remainingCredit ?? 0) >= required;
      },
    }),
    {
      name: 'vivideo-user-store',
      partialize: (state) => ({ userId: state.userId }),
    }
  )
);

